import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import usePhoneLayout from '../hooks/usePhoneLayout';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LuSend, LuX, LuArrowLeft, LuArrowRight, LuPencil, LuSparkles, LuHouse } from 'react-icons/lu';
import { LINA_AI_ENABLED, sendLinaAiMessage } from '../Services/linaAiService';
import { waitMinutes } from '../utils/retryAfter';
import VoiceInputButton, { appendDictation } from './VoiceInputButton';
import './LinaAiChat.css';

function ListingCard({ item, vacation, t }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [item.imageUrl]);
  return <article className="lina-ai-card lina-ai-listing">
    <div className="lina-ai-photo">
      {item.imageUrl && !failed ? <img src={item.imageUrl} alt="" loading="lazy" referrerPolicy="no-referrer" onError={() => setFailed(true)} /> : <span className="lina-ai-photo-fallback" aria-label={t('linaAi:noPhoto')} role="img"><LuHouse aria-hidden="true" /><small>Lina Immo</small></span>}
    </div>
    <div className="lina-ai-card-info">
      <strong>{item.category || t(vacation ? 'linaAi:vacation' : 'linaAi:results')} <small>{vacation ? t('linaAi:vacation') : item.type ? t(item.type) : ''}</small></strong>
      <span>{[item.city, item.delegation].filter(Boolean).join(' · ')}</span>
      {item.price !== null && <b className="lina-ai-price"><bdi>{item.price.toLocaleString()} {t(vacation ? 'linaAi:perNight' : item.type === 'rent' ? 'linaAi:perMonth' : 'linaAi:currency')}</bdi></b>}
      <div className="lina-ai-card-footer">
        {item.surface !== null && <span><bdi>{item.surface.toLocaleString()} m²</bdi></span>}
        <Link to={`/${vacation ? 'DetailsVacation' : 'listing'}/${encodeURIComponent(item.id)}`}>{t('linaAi:view')} <span className="lina-ai-link-arrow" aria-hidden="true">→</span></Link>
      </div>
    </div>
  </article>;
}

function Criteria({ criteria, t, onEdit }) {
  const entries = Object.entries(criteria).filter(([key]) => key !== 'page');
  if (!entries.length) return null;
  const label = (key, value) => {
    if (key === 'type') return t(String(value));
    if (['category', 'city', 'delegation'].includes(key)) return String(value);
    if (key === 'minPrice' || key === 'maxPrice') return `${key === 'minPrice' ? '≥' : '≤'} ${Number(value).toLocaleString()} ${t('linaAi:currency')}`;
    return `${t(key)} : ${value}`;
  };
  return <section className="lina-ai-criteria" aria-label={t('linaAi:yourSearch')}>
    <div><strong>{t('linaAi:yourSearch')}</strong><button onClick={onEdit}><LuPencil aria-hidden="true" />{t('linaAi:edit')}</button></div>
    <ul>{entries.map(([key, value]) => <li key={key}><bdi dir={key === 'minPrice' || key === 'maxPrice' ? 'ltr' : 'auto'}>{label(key, value)}</bdi></li>)}</ul>
  </section>;
}

function Results({ response, t }) {
  const card = (item, vacation = false) => <ListingCard key={item.id} item={item} vacation={vacation} t={t} />;
  const quote = response.priceCalculation;
  return <>
    {response.listings.map(item => card(item))}
    {response.vacations.map(item => card(item, true))}
    {response.comparison.length > 0 && <section aria-label={t('linaAi:comparison')}><h3>{t('linaAi:comparison')}</h3>{response.comparison.map(item => card(item))}</section>}
    {quote && <section className="lina-ai-card" aria-label={t('linaAi:estimate')}>
      <h3>{t('linaAi:estimate')}</h3>
      {(quote.arrival || quote.departure) && <p>{t('linaAi:dates')} : <bdi>{quote.arrival} → {quote.departure}</bdi></p>}
      {quote.nights !== null && <p>{t('linaAi:nights')} : {quote.nights}</p>}
      {quote.nightlyPrice !== null && <p>{quote.nightlyPrice} {t('linaAi:perNight')}</p>}
      {quote.discount !== null && <p>{t('linaAi:discount')} : {quote.discount}%</p>}
      <strong>{t('linaAi:total')} : <bdi>{quote.total.toLocaleString()} TND</bdi></strong>
      <small>{t('linaAi:availability')}</small>
    </section>}
  </>;
}

// Actions and fields the floating button must never cover on mobile.
const PRIMARY_ACTIONS = [
  ".lina-home-stats > div > div", ".lina-home main h2", ".lina-home main a.group", ".lina-home main ul > li",
  ".lina-home-search button", ".lina-home-search select", ".lina-home-search input", ".lina-m-secondary", ".lina-m-popular a", ".lina-m-popular button",
  ".lina-vacation-search input", ".lina-vacation-search select", ".lina-vacation-search button", ".lina-m-langbar button",
  ".lina-m-filters-trigger", ".lina-results-toggle button", "main select", "[data-filter-apply]", ".listing-photo-action",
  "aside button", "aside a[href^=\"tel:\"]", "aside input", "aside select", "aside textarea",
].join(",");

export default function LinaAiChat({ enabled = LINA_AI_ENABLED }) {
  const { t, i18n } = useTranslation('linaAi');
  const [open, setOpen] = useState(false), [input, setInput] = useState('');
  const [history, setHistory] = useState([]), [loading, setLoading] = useState(false), [error, setError] = useState('');
  const session = useRef(null), pending = useRef(null), revision = useRef(0), field = useRef(null), trigger = useRef(null), end = useRef(null);
  const conversation = useRef(null), latest = useRef(null), root = useRef(null);
  // Per-IP limit with a known wait (Retry-After): the message gives the minutes, typing stays possible, nothing is resent before the end.
  const [wait, setWait] = useState({ minutes: 0, until: 0 });
  useEffect(() => {
    if (!wait.until) return undefined;
    const timer = setTimeout(() => setWait({ minutes: 0, until: 0 }), Math.max(0, wait.until - Date.now()));
    return () => clearTimeout(timer);
  }, [wait.until]);
  const { pathname } = useLocation();
  const phone = usePhoneLayout();
  const [homeTarget, setHomeTarget] = useState(null);
  useEffect(() => { setHomeTarget(phone && pathname === '/' ? document.getElementById('phone-home-ai') : null); }, [phone, pathname]);
  // Mobile placement of the floating button: lifted above a primary action, or set aside if no free spot.
  const [placement, setPlacement] = useState({ lift: 0, aside: false });
  // Navigating (e.g. opening a suggested listing) closes the assistant.
  useEffect(() => { setOpen(false); }, [pathname]);
  // Mobile: never cover a primary action. Move the button to the nearest free spot (up or down, between
  // the header and the bottom edge); only when none exists is it set aside (transparent, not tappable).
  useEffect(() => {
    const query = window.matchMedia?.('(max-width: 640px)');
    const place = next => {
      setPlacement(current => (current.lift === next.lift && current.aside === next.aside ? current : next));
    };
    if (open || !query) { place({ lift: 0, aside: false }); return undefined; }
    let frame = 0;
    const measure = () => {
      frame = 0;
      const button = trigger.current;
      if (!button || !query.matches) { place({ lift: 0, aside: false }); return; }
      const r = button.getBoundingClientRect();
      // The container is not transformed: it gives the resting position even during the lift transition.
      const rest = root.current?.getBoundingClientRect() ?? r;
      const top = rest.top, bottom = rest.bottom;
      const actions = [...document.querySelectorAll(PRIMARY_ACTIONS)]
        .filter(element => !button.contains(element))
        .map(element => element.getBoundingClientRect())
        .filter(b => b.width > 0 && b.left < r.right && b.right > r.left);
      const covers = lift => actions.some(b => b.top < bottom - lift + 6 && b.bottom > top - lift - 6);
      const header = document.querySelector('header')?.getBoundingClientRect().bottom ?? 0;
      const maxUp = top - header - 8, maxDown = window.innerHeight - bottom - 16;
      let lift;
      for (let distance = 0; lift === undefined && distance <= Math.max(maxUp, maxDown); distance += 4) {
        if (distance <= maxUp && !covers(distance)) lift = distance;
        else if (distance <= maxDown && !covers(-distance)) lift = -distance;
      }
      place(lift === undefined ? { lift: 0, aside: true } : { lift, aside: false });
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(measure); };
    schedule();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    const layout = window.ResizeObserver ? new ResizeObserver(schedule) : null;
    layout?.observe(document.body);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      layout?.disconnect();
    };
  }, [open, pathname]);
  // Mobile bottom-sheet follows the visual viewport so the input stays above the iOS keyboard.
  useEffect(() => {
    const viewport = window.visualViewport, node = root.current;
    if (!open || !viewport || !node) return undefined;
    const update = () => {
      node.style.setProperty('--lina-ai-vh', `${viewport.height}px`);
      node.style.setProperty('--lina-ai-vtop', `${viewport.offsetTop}px`);
    };
    update();
    viewport.addEventListener('resize', update);
    viewport.addEventListener('scroll', update);
    return () => {
      viewport.removeEventListener('resize', update);
      viewport.removeEventListener('scroll', update);
      node.style.removeProperty('--lina-ai-vh');
      node.style.removeProperty('--lina-ai-vtop');
    };
  }, [open]);
  useEffect(() => () => { revision.current++; pending.current?.abort(); }, []);
  useEffect(() => { if (open) field.current?.focus(); }, [open]);
  useEffect(() => {
    if (open && conversation.current) {
      conversation.current.scrollTop = latest.current?.offsetTop || 0;
    }
  }, [history, loading, open]);
  useEffect(() => {
    if (field.current) {
      field.current.style.height = 'auto';
      field.current.style.height = `${input ? Math.min(field.current.scrollHeight, 80) : 38}px`;
    }
  }, [input, open]);
  if (!enabled) return null;
  const close = () => { setOpen(false); trigger.current?.focus(); };
  const currentResponse = [...history].reverse().find(entry => entry.response)?.response;
  const edit = () => { if (conversation.current) conversation.current.scrollTop = 0; field.current?.focus(); };
  const messageText = entry => {
    const response = entry.response;
    if (!response || response.clarification || response.priceCalculation || response.comparison.length) return entry.message;
    return t('linaAi:found', { count: response.listings.length + response.vacations.length });
  };
  const reset = () => {
    revision.current++; pending.current?.abort(); pending.current = null; session.current = null;
    setHistory([]); setError(''); setLoading(false); setInput(''); field.current?.focus();
  };
  const send = async (value = input) => {
    const message = value.trim();
    if (!message || message.length > 1000 || pending.current || wait.until > Date.now()) return;
    const token = ++revision.current, controller = new AbortController();
    pending.current = controller; setLoading(true); setError(''); setInput('');
    setHistory(previous => [...previous, { role: 'user', message }].slice(-40));
    const timeout = setTimeout(() => controller.abort(), 30000);
    try {
      const response = await sendLinaAiMessage({ message, sessionId: session.current, language: i18n.resolvedLanguage || i18n.language, signal: controller.signal });
      if (revision.current !== token) return;
      session.current = response.sessionId;
      setHistory(previous => [...previous, { role: 'assistant', message: response.message || t(response.clarification ? 'linaAi:clarification' : 'linaAi:received'), response }].slice(-40));
    } catch (failure) {
      if (revision.current !== token) return;
      const limited = failure.message === 'rate_limited' && failure.retryAfter > 0;
      const code = limited ? 'rateLimitedWait'
        : { lina_ai_disabled: 'disabled', provider_not_configured: 'disabled', session_expired: 'expired', lina_ai_timeout: 'timeout', lina_ai_busy: 'busy' }[failure.message] || (failure.name === 'AbortError' ? 'timeout' : 'generic');
      if (limited) setWait({ minutes: waitMinutes(failure.retryAfter), until: Date.now() + failure.retryAfter * 1000 });
      setError(code); setInput(message);
    } finally {
      clearTimeout(timeout);
      if (revision.current === token) { pending.current = null; setLoading(false); }
    }
  };
  return <>{homeTarget && createPortal(<section className="phone-ai-card" aria-label={t('linaAi:title')} dir={i18n.dir()}>
    <header><span aria-hidden="true">L</span><div><h2>{t('linaAi:title')}</h2><p>{t('translation:mobileFinal.aiIntro')}</p></div></header>
    <form onSubmit={event => { event.preventDefault(); setOpen(true); send(); }}>
      <div className="phone-ai-input"><input aria-label={t('linaAi:placeholder')} placeholder={t('translation:mobileFinal.aiExample')} maxLength={1000} value={input} onChange={event => setInput(event.target.value)} disabled={loading} /><VoiceInputButton disabled={loading} onText={text => setInput(previous => appendDictation(previous, text, 1000))} /></div>
      <button type="submit" disabled={loading || wait.until > 0}>{t('translation:propertyRequest.home.start')}</button>
    </form>
  </section>, homeTarget)}<div ref={root} className="lina-ai" dir={i18n.dir()} data-open={open || undefined}>
    <button ref={trigger} className={`lina-ai-trigger${placement.aside ? ' lina-ai-dodge' : ''}`} style={placement.lift ? { transform: `translateY(${-placement.lift}px)` } : undefined} aria-label={t('linaAi:title')} aria-expanded={open} aria-controls="lina-ai-panel" onClick={() => setOpen(value => !value)}><LuSparkles aria-hidden="true" /> <span className="lina-ai-trigger-label">{t('linaAi:trigger')}</span></button>
    {open && <div className="lina-ai-backdrop" aria-hidden="true" onClick={close} />}
    {open && <section id="lina-ai-panel" className="lina-ai-panel" role="dialog" aria-labelledby="lina-ai-title" onKeyDown={event => { if (event.key === 'Escape') close(); }}>
      <header>
        <span className="lina-ai-avatar" aria-hidden="true">L</span>
        <div className="lina-ai-heading"><h2 id="lina-ai-title">{t('linaAi:title')}</h2><small><span className="lina-ai-online" />{t('linaAi:online')}</small></div>
        <button onClick={edit} aria-label={t('linaAi:editSearch')} title={t('linaAi:editSearch')}>{i18n.dir() === 'rtl' ? <LuArrowRight aria-hidden="true" /> : <LuArrowLeft aria-hidden="true" />}</button>
        <button onClick={close} aria-label={t('linaAi:close')} title={t('linaAi:close')}><LuX aria-hidden="true" /></button>
      </header>
      <div ref={conversation} className="lina-ai-history" role="log" aria-live="polite" aria-relevant="additions">
        <div className="lina-ai-search-actions"><button onClick={reset}>{t('linaAi:newSearch')}</button></div>
        {currentResponse && <Criteria criteria={currentResponse.criteria} t={t} onEdit={edit} />}
        {history.length === 0 && <p className="lina-ai-welcome">{t('linaAi:welcome')}</p>}
        {history.length === 0 && <div className="lina-ai-suggestions">{t('linaAi:suggestions', { returnObjects: true }).map(suggestion => <button key={suggestion} onClick={() => send(suggestion)}>{suggestion}</button>)}</div>}
        {history.map((entry, index) => <div ref={index === history.length - 1 ? latest : null} className={`lina-ai-message ${entry.role}`} key={index}><p>{messageText(entry)}</p>{entry.response && <Results response={entry.response} t={t} />}</div>)}
        {loading && <p role="status">{t('linaAi:loading')}</p>}
        {error && <p role="alert">{t(`errors.${error}`, error === 'rateLimitedWait' ? { count: wait.minutes || 1 } : undefined)}</p>}
        <div ref={end} />
      </div>
      <form onSubmit={event => { event.preventDefault(); send(); }}>
        <label className="sr-only" htmlFor="lina-ai-input">{t('linaAi:placeholder')}</label>
        <textarea ref={field} id="lina-ai-input" dir="auto" rows="1" maxLength={1000} value={input} onChange={event => setInput(event.target.value)} onKeyDown={event => { if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) { event.preventDefault(); send(); } }} placeholder={t('linaAi:placeholder')} disabled={loading} />
        <VoiceInputButton disabled={loading} onText={text => setInput(previous => appendDictation(previous, text, 1000))} />
        <button type="submit" aria-label={t('linaAi:send')} title={t('linaAi:send')} disabled={loading || !input.trim() || wait.until > 0}><LuSend aria-hidden="true" /></button>
      </form>
    </section>}
  </div></>;
}
